import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to extract POC name from email
const extractPocName = (email) => {
  if (!email) return '';
  const name = email.split('@')[0];
  return name.charAt(0).toUpperCase() + name.slice(1);
};

// Helper to format deal owner display (like Dashboard)
const formatDealOwner = (supportPoc, salesPoc) => {
  const names = [];
  if (supportPoc) names.push(extractPocName(supportPoc));
  if (salesPoc) names.push(extractPocName(salesPoc));
  return names.length > 0 ? names.join(' & ') : '—';
};

export function AlertsPage() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState({
    zero_activity: [],
    overdue_follow_ups: []
  });
  const [loading, setLoading] = useState(true);
  const [dealOwnerFilter, setDealOwnerFilter] = useState('all');
  const [dealOwnerOptions, setDealOwnerOptions] = useState([]);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/alerts`, {
        withCredentials: true
      });
      setAlerts(data);
      
      // Extract unique deal owner names from zero_activity alerts
      const ownerSet = new Set();
      data.zero_activity.forEach(alert => {
        if (alert.support_poc) ownerSet.add(extractPocName(alert.support_poc));
        if (alert.sales_poc) ownerSet.add(extractPocName(alert.sales_poc));
      });
      setDealOwnerOptions(Array.from(ownerSet).sort());
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter zero_activity alerts by deal owner
  const filteredZeroActivity = alerts.zero_activity.filter(alert => {
    if (dealOwnerFilter === 'all') return true;
    const supportName = extractPocName(alert.support_poc);
    const salesName = extractPocName(alert.sales_poc);
    return supportName === dealOwnerFilter || salesName === dealOwnerFilter;
  });

  if (loading) {
    return (
      <Layout>
        <div className="text-center text-[#52525B] py-8">Loading alerts...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-[1600px] mx-auto">
        <h1 className="text-4xl tracking-tighter font-bold font-headings text-[#09090B] mb-8">
          Alerts
        </h1>

        {/* Zero Activity Section */}
        <section className="mb-8">
          <div className="bg-white border border-[#E4E4E7] p-4 mb-2">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl tracking-tight font-semibold font-headings text-[#09090B]">
                Zero Activity
                <span className="ml-2 text-sm text-[#52525B] font-normal">
                  ({filteredZeroActivity.length} firms)
                </span>
              </h2>
              
              {/* Deal Owner Filter */}
              <div className="flex items-center gap-3">
                <label className="text-sm text-[#52525B] font-medium">Filter by Deal Owner:</label>
                <select
                  value={dealOwnerFilter}
                  onChange={(e) => setDealOwnerFilter(e.target.value)}
                  className="px-3 py-1.5 border border-[#E4E4E7] text-sm text-[#09090B] bg-white focus:outline-none focus:ring-2 focus:ring-[#0C9168]"
                >
                  <option value="all">All Deal Owners</option>
                  {dealOwnerOptions.map(owner => (
                    <option key={owner} value={owner}>{owner}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-sm text-[#52525B] mt-1">
              Firms silent for more than 5 days (no activity)
            </p>
          </div>

          {filteredZeroActivity.length > 0 ? (
            <div className="bg-white border border-[#E4E4E7]">
              <table className="w-full border-collapse" data-testid="zero-activity-table">
                <thead>
                  <tr className="bg-[#F4F4F5] border-b border-[#E4E4E7]">
                    <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Firm Name</th>
                    <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Last Active Date</th>
                    <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Days Inactive</th>
                    <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Deal Owner</th>
                    <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Last Follow-up Note</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredZeroActivity.map((alert) => (
                    <tr
                      key={alert.site_url}
                      className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA] transition-colors cursor-pointer"
                      onClick={() => navigate(`/firm/${encodeURIComponent(alert.site_url)}`)}
                      data-testid="alert-row"
                    >
                      <td className="p-3 text-sm text-[#09090B] font-medium">
                        <div className="flex items-center gap-2">
                          {alert.firm_name}
                          <ArrowRight className="w-3 h-3 text-[#52525B]" strokeWidth={1.5} />
                        </div>
                      </td>
                      <td className="p-3 text-sm text-[#52525B]">
                        {alert.last_active_date ? new Date(alert.last_active_date).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="p-3 text-sm text-[#DC2626] font-bold font-mono">
                        {alert.days_inactive != null ? alert.days_inactive : '—'}
                      </td>
                      <td className="p-3 text-sm text-[#09090B]">
                        {formatDealOwner(alert.support_poc, alert.sales_poc)}
                      </td>
                      <td className="p-3 text-sm text-[#52525B] max-w-md truncate">
                        {alert.last_follow_up_note || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white border border-[#E4E4E7] p-6 flex items-center justify-center text-[#0C9168]">
              <CheckCircle className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {dealOwnerFilter === 'all' ? 'All clear.' : `No zero-activity alerts for ${dealOwnerFilter}.`}
            </div>
          )}
        </section>

        {/* Overdue Follow-ups Section */}
        <section className="mb-8">
          <div className="bg-white border border-[#E4E4E7] p-4 mb-2">
            <h2 className="text-xl tracking-tight font-semibold font-headings text-[#09090B]">
              Overdue Follow-ups
              <span className="ml-2 text-sm text-[#52525B] font-normal">
                ({alerts.overdue_follow_ups.length} firms)
              </span>
            </h2>
            <p className="text-sm text-[#52525B] mt-1">
              Follow-ups that are past their scheduled date
            </p>
          </div>

          {alerts.overdue_follow_ups.length > 0 ? (
            <div className="bg-white border border-[#E4E4E7]">
              <table className="w-full border-collapse" data-testid="overdue-follow-ups-table">
                <thead>
                  <tr className="bg-[#F4F4F5] border-b border-[#E4E4E7]">
                    <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Firm Name</th>
                    <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Follow-up Was Due</th>
                    <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Days Overdue</th>
                    <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Assigned To</th>
                    <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Last Note</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.overdue_follow_ups.map((alert) => (
                    <tr
                      key={alert.site_url}
                      className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA] transition-colors cursor-pointer"
                      onClick={() => navigate(`/firm/${encodeURIComponent(alert.site_url)}`)}
                      data-testid="alert-row"
                    >
                      <td className="p-3 text-sm text-[#09090B] font-medium">
                        <div className="flex items-center gap-2">
                          {alert.firm_name}
                          <ArrowRight className="w-3 h-3 text-[#52525B]" strokeWidth={1.5} />
                        </div>
                      </td>
                      <td className="p-3 text-sm text-[#DC2626] font-medium">
                        {new Date(alert.follow_up_due_date).toLocaleDateString()}
                      </td>
                      <td className="p-3 text-sm text-[#DC2626] font-bold font-mono">
                        {alert.days_overdue}
                      </td>
                      <td className="p-3 text-sm text-[#09090B]">{alert.assigned_to || '—'}</td>
                      <td className="p-3 text-sm text-[#52525B] max-w-md truncate">
                        {alert.last_note || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white border border-[#E4E4E7] p-6 flex items-center justify-center text-[#0C9168]">
              <CheckCircle className="w-5 h-5 mr-2" strokeWidth={1.5} />
              All clear.
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}